import React, { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import enGB from "date-fns/locale/en-GB";
import { HiOutlineCalendarDays, HiOutlineChevronDown } from "react-icons/hi2";
import { addMonths, changeDateToYearMonthDay, classNames } from "Utils/utils";
import {
  CARE_WATCH_MAX_RANGE_MONTHS,
  clampCareWatchToDate,
} from "../utils/constants";
import "components/common/DropDown/ThemedDropdown.css";

enGB.options = { ...enGB.options, weekStartsOn: 1 };
registerLocale("en-GB", enGB);

/** "YYYY-MM-DD" is a calendar day, not a UTC instant. */
const parseFilterDay = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatRangeLabel = (from, to) => {
  // "All dates" means no range is applied. The queue stays as it is until
  // both the start and the end have been chosen, including the same day.
  if (!from || !to) return "All dates";
  const opts = { day: "numeric", month: "short" };
  const fromStr = from.toLocaleDateString("en-GB", opts);
  const toStr = to.toLocaleDateString("en-GB", {
    ...opts,
    year: "numeric",
  });
  return `${fromStr} – ${toStr}`;
};

const sameCalendarDay = (left, right) =>
  left &&
  right &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const formatPendingStart = (start) => {
  const fromStr = start.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
  return `${fromStr} –`;
};

const CareWatchFilterDateRange = ({
  fromDate,
  toDate,
  onChange,
  className = "",
}) => {
  const datePickerRef = useRef(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setDateRange([parseFilterDay(fromDate), parseFilterDay(toDate)]);
  }, [fromDate, toDate]);

  const [startDate, endDate] = dateRange;
  const appliedStart = parseFilterDay(fromDate);
  const appliedEnd = parseFilterDay(toDate);
  const choosingEnd = isOpen && startDate && !endDate;
  const label = choosingEnd
    ? formatPendingStart(startDate)
    : formatRangeLabel(appliedStart, appliedEnd);

  const commit = (start, end) => {
    const fromDate = start ? changeDateToYearMonthDay(start) : null;
    const toDate = end ? changeDateToYearMonthDay(end) : null;
    onChange({
      fromDate,
      toDate: clampCareWatchToDate(fromDate, toDate),
    });
  };

  // The 3-month cap applies only while choosing the end date. If both ends
  // are already set, locking maxDate to the old start stops a new range
  // that begins later (same bug as a hidden To after Clear on mobile).
  const rangeMaxDate = choosingEnd
    ? addMonths(startDate, CARE_WATCH_MAX_RANGE_MONTHS)
    : undefined;

  const CustomInput = React.forwardRef(({ onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      className={classNames(
        "themed-dropdown-trigger inline-flex w-full items-center gap-2 px-[11px] text-left focus:outline-none",
        isOpen && "is-open",
        Boolean(fromDate && toDate) && "is-applied"
      )}
    >
      <HiOutlineCalendarDays
        className="h-3.5 w-3.5 shrink-0 text-[#64748B]"
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate whitespace-nowrap text-[11px] font-medium leading-none text-[#0F172A]">
        {label}
      </span>
      <HiOutlineChevronDown
        className={classNames(
          "h-3.5 w-3.5 shrink-0 text-[#94A3B8] transition-transform duration-150",
          isOpen && "rotate-180"
        )}
        aria-hidden
      />
    </button>
  ));

  CustomInput.displayName = "CareWatchFilterDateRangeInput";

  return (
    <div className={classNames("min-w-0", className)}>
      <p className="care-watch-filters-label">Date range (Max 3 months)</p>
      <DatePicker
        ref={datePickerRef}
        wrapperClassName="care-watch-filter-date-picker w-full"
        selectsRange
        startDate={startDate}
        endDate={endDate}
        maxDate={rangeMaxDate}
        openToDate={startDate || new Date()}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => {
          setIsOpen(false);
          // A start with no end is not a filter. Put the calendar back to
          // the range that is actually applied.
          setDateRange([parseFilterDay(fromDate), parseFilterDay(toDate)]);
        }}
        onChange={(selectedDates) => {
          const start = selectedDates?.[0] || null;
          const end = selectedDates?.[1] || null;

          if (!start && !end) {
            // Never allow the date filter to be removed entirely — the
            // 3-month-max rule requires a range to always be applied.
            // Reset the picker back to the currently applied dates.
            setDateRange([parseFilterDay(fromDate), parseFilterDay(toDate)]);
            return;
          }

          // The first click only marks the start. The list stays as it is
          // until the end date is chosen too — the same day counts.
          if (start && !end) {
            setDateRange([start, null]);
            return;
          }

          setDateRange([start, end]);
          commit(start, end);
        }}
        locale="en-GB"
        dateFormat="d MMM yyyy"
        // Range mode has no `selected` date, so the library marks the
        // keyboard day instead. Changing month keeps that day number and
        // paints the start date as selected in every month.
        dayClassName={(date) =>
          sameCalendarDay(date, startDate) ? "cw-range-start" : undefined
        }
        customInput={<CustomInput />}
        popperPlacement="bottom-start"
        popperClassName="care-watch-filter-date-popper"
      />
    </div>
  );
};

export default CareWatchFilterDateRange;
